// src/services/purgeCore.js
//
// PORTABLE MODULE — copy this file as-is into any Firebase (v9 modular SDK) project.
// Do not edit this file per-app; configure it via createPurgeService(firestore, config)
// in that app's own thin wrapper instead. The collection list, order, and field
// names are the ONLY thing that changes between applications.

import {
  collection, query, where, getDocs, doc, deleteDoc, addDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';

/**
 * @param {Firestore} firestore
 * @param {Object} config
 * @param {string} [config.tenantCollection='Tenants']
 * @param {Array<{collection: string, tenantField: string|null}>} config.collectionOrder
 *   Ordered list of collections to purge, from "most detailed / leaf data" to
 *   "master record" — the master record's own collection should be LAST.
 *   tenantField: the field name on each document that holds the tenant ID.
 *   Pass tenantField: null for the master collection itself, where the
 *   document ID *is* the tenant ID (no field lookup needed).
 * @param {string} [config.purgeLogCollection='PurgeLogs']
 */
export function createPurgeService(firestore, config) {
  const TENANT_COLLECTION = config.tenantCollection || 'Tenants';
  const COLLECTION_ORDER = config.collectionOrder; // required, no default — every app must define its own
  const PURGE_LOG_COLLECTION = config.purgeLogCollection || 'PurgeLogs';

  if (!Array.isArray(COLLECTION_ORDER) || COLLECTION_ORDER.length === 0) {
    throw new Error('purgeCore: config.collectionOrder is required and must list at least one collection.');
  }

  // ---- Delete every document in one collection belonging to one tenant ----
  async function purgeCollectionForTenant(collectionName, tenantField, tenantId) {
    let docsToDelete = [];

    if (tenantField === null) {
      // Master collection — the document ID itself is the tenant ID.
      docsToDelete = [doc(firestore, collectionName, tenantId)];
    } else {
      const q = query(collection(firestore, collectionName), where(tenantField, '==', tenantId));
      const snap = await getDocs(q);
      docsToDelete = snap.docs.map((d) => d.ref);
    }

    let deletedCount = 0;
    for (const ref of docsToDelete) {
      await deleteDoc(ref);
      deletedCount += 1;
    }

    console.info(`[purgeCore] ${collectionName}: deleted ${deletedCount} document(s) for tenant ${tenantId}.`);
    return deletedCount;
  }

  // ---- Purge one tenant across every configured collection, in order ----
  async function purgeTenant(tenantId, triggeredByEmail) {
    console.info(`[purgeCore] Starting purge for tenant ${tenantId}, triggered by ${triggeredByEmail}.`);

    const collectionsProcessed = [];
    let overallStatus = 'success';
    let errorDetails = '';

    for (const entry of COLLECTION_ORDER) {
      try {
        const deletedCount = await purgeCollectionForTenant(entry.collection, entry.tenantField, tenantId);
        collectionsProcessed.push({ collection: entry.collection, docsDeleted: deletedCount });
      } catch (err) {
        console.error(`[purgeCore] Failed purging ${entry.collection} for tenant ${tenantId}:`, err);
        collectionsProcessed.push({ collection: entry.collection, docsDeleted: 0, error: err.message });
        overallStatus = 'partial_failure';
        errorDetails += `${entry.collection}: ${err.message}. `;
        // Stop here — don't delete the master record if a detail collection failed,
        // to avoid orphaning data we couldn't clean up.
        break;
      }
    }

    await addDoc(collection(firestore, PURGE_LOG_COLLECTION), {
      tenantId,
      purgedAt: serverTimestamp(),
      triggeredBy: triggeredByEmail || 'unknown',
      collectionsProcessed,
      status: overallStatus,
      errorDetails: errorDetails || null,
    });

    return { tenantId, status: overallStatus, collectionsProcessed };
  }

  // ---- Find every tenant whose 30-day mark has already passed ----
  async function findTenantsDueForPurge() {
    const now = Timestamp.now();
    const q = query(
      collection(firestore, TENANT_COLLECTION),
      where('status', '==', 'marked_for_deletion'),
      where('deletionScheduledFor', '<=', now)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  // ---- Run the full sweep: find due tenants, purge each one ----
  async function runScheduledPurge(triggeredByEmail) {
    const dueTenants = await findTenantsDueForPurge();
    console.info(`[purgeCore] ${dueTenants.length} tenant(s) due for purge.`);

    const results = [];
    for (const tenant of dueTenants) {
      const result = await purgeTenant(tenant.id, triggeredByEmail);
      results.push(result);
    }
    return results;
  }

  // ---- Read past purge logs (for audit / review) ----
  async function getPurgeLogs() {
    const snap = await getDocs(collection(firestore, PURGE_LOG_COLLECTION));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  return {
    purgeTenant,
    findTenantsDueForPurge,
    runScheduledPurge,
    getPurgeLogs,
  };
}